import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
} from "@/components/layout/PageShell";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  REPORT_CATEGORIES,
  REPORT_CENTER_ITEMS,
  resolveActiveReportId,
} from "@/lib/reportCenterNav";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { isMultiBranchFeatureEnabled } from "@/lib/permissions";
import { ReportBranchProvider, ReportBranchSelect } from "@/contexts/ReportBranchContext";

export default function ReportCenterLayout() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [filter, setFilter] = useState("All");
  const activeId = resolveActiveReportId(pathname);
  const multiBranch = isMultiBranchFeatureEnabled(user);

  const catalog = multiBranch
    ? REPORT_CENTER_ITEMS
    : REPORT_CENTER_ITEMS.filter((r) => r.id !== "branch-comparison");

  const visibleReports =
    filter === "All"
      ? catalog
      : catalog.filter((r) => r.category === filter);

  return (
    <ReportBranchProvider>
      <PageContainer className="!max-w-none">
      <PageHeader
        title="Report Center"
        subtitle="Sales, operations, inventory, customers, suppliers and accounting reports"
        actions={<ReportBranchSelect />}
      />

      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-4 items-start">
        <Card className="p-0 xl:sticky xl:top-4">
          <div className="px-3 py-2.5 border-b">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-9 text-[12.5px]" data-testid="rpt-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REPORT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ul className="p-2 space-y-0.5 max-h-[calc(100vh-220px)] overflow-auto">
            <li>
              <NavLink
                to="/reports"
                end
                className={({ isActive }) =>
                  cn(
                    "w-full flex items-center gap-2.5 px-2.5 h-9 rounded-md text-[12.5px] transition-colors",
                    isActive
                      ? "bg-blue-50 text-blue-700 font-medium border border-blue-200"
                      : "text-slate-700 hover:bg-slate-50 border border-transparent",
                  )
                }
                data-testid="rpt-home"
              >
                <BarChart3 size={14} className="text-slate-500" />
                <span className="flex-1">Overview</span>
              </NavLink>
            </li>
            {visibleReports.map((r) => {
              const Icon = r.icon;
              return (
                <li key={r.id}>
                  <NavLink
                    to={r.path}
                    className={cn(
                      "w-full flex items-center gap-2.5 px-2.5 h-9 rounded-md text-[12.5px] transition-colors",
                      activeId === r.id
                        ? "bg-blue-50 text-blue-700 font-medium border border-blue-200"
                        : "text-slate-700 hover:bg-slate-50 border border-transparent",
                    )}
                    data-testid={`rpt-${r.id}`}
                  >
                    <Icon size={14} className="text-slate-500 shrink-0" />
                    <span className="flex-1 truncate">{r.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </Card>

        <div className="min-w-0" data-report-root>
          <Outlet />
        </div>
      </div>
      </PageContainer>
    </ReportBranchProvider>
  );
}
